/**
 * The navigation model: the ten operational areas the sidebar is organised
 * around, the boards that actually exist behind them, and the one guard that
 * keeps the sidebar from ever rendering a link to a route that has no page.
 *
 * Three exports carry the whole design:
 *
 * - `BUILT_PATHS` — every path `App.tsx` has a page component for. The single
 *   source of truth for "is there something behind this link?". This is the
 *   same device `AI_BUILT_PATHS` / `isAiRouteBuilt` uses for the `/ai/*`
 *   vocabulary (`features/ai/params.ts`), lifted to nav level: an area may
 *   *declare* where it wants to point long before the page exists, and the
 *   guard — not a human's memory — decides whether the nav renders a link or a
 *   disabled row.
 *
 * - `NAV_AREAS` — the ten areas, in the order the user specified them, plus one
 *   clearly-labelled extra group for the boards that do not map onto any of the
 *   ten. Five of the ten have nothing behind them today, and they say so: each
 *   carries the integration it is waiting on, because the gap is information.
 *
 * - `NAV_SECTIONS` — the board registry: one entry per shipped board, with the
 *   label and description that board's page uses as its own subtitle.
 *
 * **`NAV_SECTIONS` is load-bearing beyond the nav.** Seven feature boards read
 * their page subtitle from it (`NAV_SECTIONS.find((s) => s.label === "Cloud")`
 * and friends in `features/<board>/index.tsx`), so an entry's `label`, `path` and
 * `description` are a contract with those pages, not private nav data. Entries
 * may be added; the existing eleven must keep their wording.
 *
 * **One nav model, no second switcher.** A board's own screens are registry
 * entries carrying `parent`, which the rail renders as a nested group under the
 * board (`boardsForArea` → `childBoards`). That is what retired the Dynamics
 * feature's hand-built `DynamicsSubNav`: a route switcher inside a feature is a
 * second navigation that drifts from this one, and the reader then has two
 * places to look for the same six screens.
 *
 * **Two gates, two behaviours.** `adminOnly` hides a row (the route's guard
 * would bounce the reader); `investigatorOnly` disables it with a sentence (the
 * page renders, its payloads answer 403). Both decisions live in
 * `./nav-access.ts`, and the investigator half is derived from what the API
 * answers — never from an address list in the frontend.
 */

import {
  BadgeCheck,
  Bot,
  Boxes,
  BrainCircuit,
  Building2,
  ClipboardList,
  Cloud,
  DatabaseBackup,
  Factory,
  FileClock,
  Gauge,
  Globe,
  Grid2x2,
  Headset,
  IdCard,
  Layers,
  LayoutDashboard,
  type LucideIcon,
  MapPin,
  MessageSquareWarning,
  Network,
  ShieldAlert,
  ShieldCheck,
  Siren,
  Sparkles,
  Target,
  TriangleAlert,
  Users,
  UserSearch,
  Workflow,
} from "@norican/icons";

import { boardVisibility, type NavViewer } from "./nav-access";

/* ── The built-route guard ──────────────────────────────────────────────── */

/**
 * Every path `App.tsx` mounts a page component on, in route order.
 *
 * Redirect-only paths (`/proofpoint`, `/dashboard`, `/home`, `/`) are
 * deliberately absent: they resolve, but nothing in the nav should aim at a
 * redirect. Shipping a new board is one string here plus its `App.tsx` route —
 * every area and sub-item that names the path starts resolving to a live link
 * with no change at any call site.
 */
export const BUILT_PATHS: readonly string[] = [
  "/overview",
  "/websites",
  "/network",
  "/cloud",
  "/cloud/alerts",
  "/cloud/changes",
  "/backup",
  "/backup/actions",
  "/backup/jobs",
  "/backup/coverage",
  "/dynamics",
  "/dynamics/forms",
  "/dynamics/requests",
  "/dynamics/queues",
  "/dynamics/pinpoint",
  "/dynamics/investigate",
  "/dynamics/reports",
  "/experience",
  "/m365",
  "/security",
  "/defender",
  "/ai/overview",
  "/ai/copilot",
  "/ai/claude",
  "/licences",
  "/licences/accounts",
] as const;

/** Whether a path the nav names has a page behind it yet. */
export function isRouteBuilt(path: string): boolean {
  return BUILT_PATHS.includes(path);
}

/* ── The board registry ─────────────────────────────────────────────────── */

/**
 * One shipped board (or board drill-down), and how it is described.
 *
 * `description` doubles as the nav hint and, for the eleven L1 boards, as the
 * page's own subtitle — see the module comment before changing any wording.
 */
export interface NavSection {
  /** Route path, also the nav link target. */
  path: string;
  /** Nav label. */
  label: string;
  /** One-line description, used as the page subtitle and the nav hint. */
  description: string;
  /** Icon shown beside the label. */
  icon: LucideIcon;
  /** Route sits behind `AdminRoute`; hidden from the nav for everyone else. */
  adminOnly?: boolean;
  /**
   * Route's payloads answer 403 unless the session is on the F&O investigator
   * list. Unlike `adminOnly` the page itself renders, so the row is *disabled
   * with an explanation* rather than hidden — see `./nav-access.ts`.
   */
  investigatorOnly?: boolean;
  /**
   * The board this one drills into, as a `path`. A section with a parent is
   * rendered as a sub-item of that section rather than of the area, so the six
   * Dynamics screens read as one board's screens instead of six siblings of
   * M365 (`boardsForArea` returns the parents, `childBoards` their children).
   */
  parent?: string;
  /**
   * A live figure the sidebar may put beside this row, named rather than
   * fetched here — this module stays a pure registry and imports no hook.
   * `app-sidebar.tsx` owns the one lookup per kind.
   */
  liveCount?: "fo-open-reports";
}

export const NAV_SECTIONS: readonly NavSection[] = [
  {
    path: "/overview",
    label: "Overview",
    description: "Executive summary across every monitored domain.",
    icon: LayoutDashboard,
  },
  {
    path: "/websites",
    label: "Websites",
    description: "Live availability of Norican's business websites.",
    icon: Globe,
  },
  {
    path: "/network",
    label: "Network",
    description: "Cato SASE sites, last-mile links and tunnels.",
    icon: Network,
  },
  {
    path: "/cloud",
    label: "Cloud",
    description: "Azure subscriptions and hosted workloads.",
    icon: Cloud,
  },
  {
    path: "/m365",
    label: "M365",
    description: "Microsoft 365 service health and active incidents.",
    icon: Grid2x2,
  },
  {
    path: "/backup",
    label: "Backup",
    description:
      "Veeam and Azure Backup: what ran, what failed, what did not run, and what is protected by nothing.",
    icon: DatabaseBackup,
  },
  {
    path: "/security",
    label: "Security",
    description:
      "Email security as leadership reads it: blocked before delivery, individually targeted, business impact, block rate.",
    icon: ShieldCheck,
  },
  {
    path: "/ai/overview",
    label: "AI",
    description:
      "The Azure AI estate: throttling, quota headroom, idle accounts and what nothing is watching.",
    icon: BrainCircuit,
  },
  {
    path: "/ai/copilot",
    label: "Copilot usage",
    description: "Microsoft 365 Copilot seats enabled against licensed, and where the use lands.",
    icon: Sparkles,
  },
  {
    path: "/ai/claude",
    label: "Claude usage",
    description: "Anthropic Claude usage and spend for the organisation.",
    icon: Bot,
  },
  {
    path: "/defender",
    label: "Defender",
    description:
      "Microsoft Defender XDR: incidents, alerts, Secure Score and identity sensor health.",
    icon: ShieldAlert,
  },
  {
    path: "/dynamics",
    label: "Dynamics",
    description: "Dynamics 365 Finance & Operations front-office health.",
    icon: Factory,
  },
  {
    // Its own section rather than a Dynamics sub-route: the board spans five
    // apps (F&O, CE, Power BI, SharePoint, Teams), so filing it under Dynamics
    // would hide four of them behind an ERP label.
    path: "/experience",
    label: "Experience",
    // Not "what the estate's browsers measure": one managed extension is the
    // only thing measuring here, and on 2026-08-22 it covered 1 of 31 F&O
    // companies. The board leads with the rollout for that reason, and the
    // door has to say the same thing the room does.
    description:
      "How much of the fleet runs the managed Edge extension, and what those browsers measured across F&O, CE, Power BI, SharePoint and Teams.",
    icon: Gauge,
  },
  {
    path: "/licences",
    label: "Licences",
    description: "Microsoft 365 seats bought, used, and sitting idle.",
    icon: BadgeCheck,
  },
  /* Drill-downs the sidebar surfaces as sub-items. Added after the eleven L1
     boards above so `find((s) => s.label === …)` in the feature pages keeps
     matching the board, never its drill-down. */
  {
    path: "/cloud/alerts",
    label: "Azure alerts",
    description: "Every Azure Monitor alert behind the cloud board's charts.",
    icon: TriangleAlert,
  },
  {
    path: "/backup/actions",
    label: "What needs you",
    description: "Every open backup finding, ranked by when it has to be dealt with.",
    icon: ClipboardList,
  },
  {
    path: "/backup/jobs",
    label: "Jobs",
    description: "Every Veeam and Azure Backup job, with its recent runs.",
    icon: Boxes,
  },
  {
    path: "/backup/coverage",
    label: "Coverage",
    description: "What backup protects, and what is protected by nothing.",
    icon: ShieldCheck,
  },
  {
    path: "/licences/accounts",
    label: "Reclaimable accounts",
    description: "Licensed accounts that look reclaimable.",
    icon: Users,
    adminOnly: true,
  },
  /* The Dynamics board's own six screens, in the order they are worth opening.
     The first four read F&O's own telemetry: Pinpoint ranks the waiting,
     Reports is the queue people put themselves in, Forms holds the measured
     percentiles, Investigate answers for one named person. The last two are the
     integration path in front of F&O, and neither carries F&O's own numbers
     today — the gateway screen shows API Management's traffic (every row the
     request queries return belongs to `NO-APIM-NAX`, which has no F&O
     `AppRoleName` filter to narrow it), and Queues has no namespace configured
     at all, so it reads nothing.

     All six carry `parent: "/dynamics"`, so the rail nests them under the board
     rather than listing them beside M365 — which is what let a bespoke
     `DynamicsSubNav` grow inside the feature in the first place. The rail is the
     navigation; there is no second switcher.

     Queues belongs *under* the gateway screen rather than beside it, and this
     registry cannot say so: `childBoards` is only ever asked about a board's own
     path, and `app-sidebar.tsx` renders a child with `BoardRow`, which does not
     recurse — a grandchild would render nowhere, so re-parenting it would delete
     a door people use. Second best, and what is here: the two stay siblings,
     adjacent and last, and the Queues description names the screen it sits
     behind. */
  {
    path: "/dynamics/pinpoint",
    label: "Pinpoint",
    description:
      "What to fix first, and what it costs: a short list of forms and companies, priced in the person-hours of waiting they cause.",
    icon: Target,
    parent: "/dynamics",
    investigatorOnly: true,
  },
  {
    path: "/dynamics/reports",
    label: "Reports",
    description: 'The queue of people who pressed "this app is slow right now".',
    icon: MessageSquareWarning,
    parent: "/dynamics",
    investigatorOnly: true,
    liveCount: "fo-open-reports",
  },
  {
    path: "/dynamics/forms",
    label: "Forms",
    description:
      "Every F&O form the ranking query returned, with its full percentiles. A reference sorted by P95; which of them is worth fixing is Pinpoint's answer.",
    icon: ClipboardList,
    parent: "/dynamics",
  },
  {
    path: "/dynamics/investigate",
    label: "Investigate",
    description:
      "One person's slow interactions, one record at a time. Reason-gated and audit-logged.",
    icon: UserSearch,
    parent: "/dynamics",
    investigatorOnly: true,
  },
  {
    // The path stays `/dynamics/requests` because bookmarks and cross-links
    // point at it; only the label and this line changed, to name the system the
    // rows actually come from.
    path: "/dynamics/requests",
    label: "API gateway",
    description:
      "API Management in front of F&O: result codes, slowest names and failing dependencies for NO-APIM-NAX. The gateway's traffic, not F&O's own.",
    icon: Workflow,
    parent: "/dynamics",
  },
  {
    path: "/dynamics/queues",
    label: "Queues",
    description:
      "Service Bus, the integration path's second half behind API gateway. No namespace is configured, so nothing is read yet.",
    icon: Layers,
    parent: "/dynamics",
  },
] as const;

/** The registry entry for a path, or `undefined` if the path is not a board. */
export function sectionByPath(path: string): NavSection | undefined {
  return NAV_SECTIONS.find((section) => section.path === path);
}

/* ── The ten operational areas ──────────────────────────────────────────── */

/**
 * Which block of the sidebar an area belongs to.
 *
 * `operational` is the user's ten areas, in their order. `other` is the honest
 * remainder: boards that ship and work today but do not map onto any of the
 * ten, and would be a regression to lose.
 */
export type NavAreaGroup = "operational" | "other";

export interface NavArea {
  /** Stable id, used as the React key and for status lookups. */
  id: string;
  /** Nav label. */
  label: string;
  /** What the area answers. */
  description: string;
  /** The system(s) the area reads, as the user named them. */
  systems: string;
  /** Icon shown beside the label. */
  icon: LucideIcon;
  /**
   * Where the area opens. `undefined` means nothing is built behind it — the
   * row renders disabled and non-navigable, never as a link to nowhere.
   */
  path?: string;
  /** The integration an unbuilt area waits on. Set exactly when `path` is not. */
  waitingOn?: string;
  /** Board paths shown as sub-items, resolved through `sectionByPath`. */
  boards?: readonly string[];
  group: NavAreaGroup;
}

/**
 * The ten areas, then the remainder.
 *
 * Five areas have no route: **Business Service Health**, **Active Major
 * Incidents**, **User Experience**, **Identity** and **Service Desk**. Three of
 * those five wait on Ivanti ITSM, which appears nowhere in this repository —
 * not a collector, not a client, not a setting — so Service Desk and Active
 * Major Incidents have no data source at all, and User Experience has no
 * transaction-failure feed. Identity waits on an Entra/AD *health* integration;
 * Entra is wired for sign-in and licence lookups only. Business Service Health
 * waits on the four systems that would make a business-service rollup possible.
 *
 * Where a working board sits inside an unbuilt area, it is listed as a sub-item
 * anyway: the rollup does not exist yet, but the component board does, and
 * hiding it to keep the row tidy would lose a shipped board.
 */
export const NAV_AREAS: readonly NavArea[] = [
  {
    id: "global-it-health",
    label: "Global IT Health",
    description: "Green, amber and red by domain across the estate.",
    systems: "All monitored systems / NOVA aggregation",
    icon: LayoutDashboard,
    path: "/overview",
    group: "operational",
  },
  {
    id: "business-service-health",
    label: "Business Service Health",
    description: "Sales, Engineering, Finance, Supply Chain and Workplace as one rollup.",
    systems: "Dynamics CRM/Sales, Dynamics F&O, Bluestar, CAD, M365, Ivanti, Fabric/IMDP",
    icon: Building2,
    waitingOn: "Dynamics CRM/Sales, Bluestar, CAD and Fabric/IMDP integration",
    boards: ["/dynamics", "/m365"],
    group: "operational",
  },
  {
    id: "major-incidents",
    label: "Active Major Incidents",
    description: "P1 and P2 incidents, with the users, sites and services they affect.",
    systems: "Ivanti ITSM + correlated monitoring",
    icon: Siren,
    waitingOn: "Ivanti ITSM integration",
    group: "operational",
  },
  {
    id: "user-experience",
    label: "User Experience",
    description: "Slow services, latency and failed transactions as users meet them.",
    systems: "Dynamics CRM, Dynamics F&O, CATO, M365, Ivanti / endpoint telemetry",
    icon: Gauge,
    waitingOn: "Ivanti ITSM and endpoint telemetry integration",
    boards: ["/websites", "/experience"],
    group: "operational",
  },
  {
    id: "sites",
    label: "Sites",
    description: "Internet, Cato SASE and per-site health.",
    systems: "CATO",
    icon: MapPin,
    path: "/network",
    group: "operational",
  },
  {
    id: "cloud",
    label: "Cloud",
    description: "Azure subscriptions and platform health.",
    systems: "Microsoft Azure",
    icon: Cloud,
    path: "/cloud",
    boards: ["/cloud/alerts"],
    group: "operational",
  },
  {
    id: "security",
    label: "Security",
    description: "Critical security incidents and exposure.",
    systems: "Microsoft Defender XDR, Proofpoint",
    icon: ShieldCheck,
    path: "/security",
    boards: ["/defender"],
    group: "operational",
  },
  {
    id: "identity",
    label: "Identity",
    description: "Authentication success and directory health.",
    systems: "Microsoft Entra ID, Active Directory",
    icon: IdCard,
    waitingOn: "Microsoft Entra ID / Active Directory health integration",
    group: "operational",
  },
  {
    id: "service-desk",
    label: "Service Desk",
    description: "Ticket surge and SLA breaches.",
    systems: "Ivanti ITSM",
    icon: Headset,
    waitingOn: "Ivanti ITSM integration",
    group: "operational",
  },
  {
    id: "changes",
    label: "Changes",
    description: "Recent changes correlated with degradation. Azure only today.",
    systems: "Azure, Dynamics F&O, Dynamics CRM, M365, Entra ID, CATO, Ivanti",
    icon: FileClock,
    path: "/cloud/changes",
    group: "operational",
  },
  {
    id: "backup",
    label: "Backup",
    description: "What ran, what failed, what did not run, and what nothing protects.",
    systems: "Veeam, Azure Backup",
    icon: DatabaseBackup,
    path: "/backup",
    boards: ["/backup/actions", "/backup/jobs", "/backup/coverage"],
    group: "other",
  },
  {
    id: "ai",
    label: "AI",
    description: "The Azure AI estate, Microsoft 365 Copilot adoption and Anthropic Claude usage.",
    systems: "Azure AI services, Microsoft 365 Copilot, Anthropic Claude",
    icon: BrainCircuit,
    path: "/ai/overview",
    // Three upstreams, three pages: ARM and Azure Monitor for the Azure estate,
    // Graph's org reports for Copilot, the Anthropic Admin API for Claude. They
    // were one page with three sections, which invited reading a Copilot figure
    // as an Azure figure.
    boards: ["/ai/copilot", "/ai/claude"],
    group: "other",
  },
  {
    id: "licences",
    label: "Licences",
    description: "Microsoft 365 seats bought, used, and sitting idle.",
    systems: "Microsoft 365",
    icon: BadgeCheck,
    path: "/licences",
    boards: ["/licences/accounts"],
    group: "other",
  },
] as const;

/** The sidebar's blocks, in render order. */
export const NAV_AREA_GROUPS: readonly NavAreaGroup[] = ["operational", "other"] as const;

/** The label rendered above each block of areas. */
export const NAV_AREA_GROUP_LABEL: Record<NavAreaGroup, string> = {
  operational: "Operational areas",
  other: "Other NOVA boards",
};

/** Areas in a given sidebar block, in nav order. */
export function areasInGroup(group: NavAreaGroup): readonly NavArea[] {
  return NAV_AREAS.filter((area) => area.group === group);
}

/**
 * Whether an area can be navigated to.
 *
 * Two ways to fail: the area declares no route at all, or it declares one that
 * `App.tsx` has not mounted. Both render the same disabled row, so a route
 * deleted from `App.tsx` degrades to "not available" instead of to a 404.
 */
export function isAreaAvailable(area: NavArea): boolean {
  return area.path !== undefined && isRouteBuilt(area.path);
}

/**
 * The sub-items an area shows: its declared boards, minus anything unbuilt, and
 * minus whatever this reader's guards would bounce them off.
 *
 * `/licences/accounts` is the only admin-only route today and it is gated by
 * `AdminRoute`. SSO currently mints `role="user"` for every account (TODO 5.15),
 * so in practice this hides it from everyone — which is the correct default:
 * the nav must not offer a door the guard will bounce you off. The
 * investigator-gated routes behave differently (rendered, disabled, explained)
 * — see `./nav-access.ts` — so they are *not* filtered out here.
 *
 * A section that declares a `parent` is never returned: it belongs under that
 * board, not beside it. `childBoards` is where those come from.
 */
export function boardsForArea(area: NavArea, viewer: NavViewer): readonly NavSection[] {
  return (area.boards ?? [])
    .map(sectionByPath)
    .filter((section): section is NavSection => section !== undefined)
    .filter((section) => section.parent === undefined)
    .filter((section) => isRouteBuilt(section.path))
    .filter((section) => boardVisibility(section, viewer) !== "hidden");
}

/**
 * The screens that hang under one board, in registry order.
 *
 * The registry's own `parent` field decides this, not the path shape: a nested
 * path is not automatically a child (`/cloud/changes` is the Changes *area*,
 * and nesting it under Cloud would light the wrong row), and a child could one
 * day live at a path that is not nested at all.
 */
export function childBoards(parentPath: string, viewer: NavViewer): readonly NavSection[] {
  return NAV_SECTIONS.filter((section) => section.parent === parentPath)
    .filter((section) => isRouteBuilt(section.path))
    .filter((section) => boardVisibility(section, viewer) !== "hidden");
}

/** Every registered child path of a board, regardless of who is reading. */
function childPathsOf(parentPath: string): readonly string[] {
  return NAV_SECTIONS.filter((section) => section.parent === parentPath).map(
    (section) => section.path,
  );
}

/* ── Active-route resolution ────────────────────────────────────────────── */

/**
 * Every path an area owns: its own route first, then its sub-items' and theirs.
 *
 * The third level matters for highlighting: without it `navPathForPath` would
 * stop at `/dynamics` for `/dynamics/reports`, and the Reports row would never
 * read as current even while the reader is standing on it.
 */
function pathsOwnedBy(area: NavArea): readonly string[] {
  const boards = area.boards ?? [];
  const nested = boards.flatMap((path) => childPathsOf(path));
  return area.path === undefined ? [...boards, ...nested] : [area.path, ...boards, ...nested];
}

/** Whether `pathname` is `path` or nested under it. */
function isUnderPath(pathname: string, path: string): boolean {
  return pathname === path || pathname.startsWith(`${path}/`);
}

/**
 * The nav path that owns `pathname` — the row that should read as current.
 *
 * Longest match wins, and that is the whole point rather than a nicety:
 * `/cloud/changes` is nested under the Cloud area's `/cloud`, but it is the
 * Changes area's own route, so a first-match-wins prefix scan would light the
 * wrong area. Nesting also means a future `/websites/:id` still resolves to
 * `/websites`.
 */
export function navPathForPath(pathname: string): string | undefined {
  let best: string | undefined;
  for (const area of NAV_AREAS) {
    for (const path of pathsOwnedBy(area)) {
      if (isUnderPath(pathname, path) && (best === undefined || path.length > best.length)) {
        best = path;
      }
    }
  }
  return best;
}

/**
 * The area a pathname belongs to, or `undefined` off-nav (e.g. `/login`).
 *
 * Resolved through `navPathForPath`, so an area highlights for its own route,
 * for its sub-items' routes, and for anything nested under either.
 */
export function areaForPath(pathname: string): NavArea | undefined {
  const activePath = navPathForPath(pathname);
  if (activePath === undefined) {
    return undefined;
  }
  return NAV_AREAS.find((area) => pathsOwnedBy(area).includes(activePath));
}

/**
 * The board registry entry a pathname belongs to, or `undefined` off-board.
 *
 * Kept alongside `areaForPath` because a board's own identity and the area it
 * hangs under are different questions, and both have callers.
 */
export function sectionForPath(pathname: string): NavSection | undefined {
  let best: NavSection | undefined;
  for (const section of NAV_SECTIONS) {
    if (
      isUnderPath(pathname, section.path) &&
      (best === undefined || section.path.length > best.path.length)
    ) {
      best = section;
    }
  }
  return best;
}
